import { yupResolver } from "@hookform/resolvers/yup";
import { MoreVertRounded } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Menu, MenuItem } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import CustomLoader from "../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../util/state/globalVariables";
import { execBookingAction, useBookingByIdWithMetaQueryObserver, useBookingNotesQuery } from "../../../../api/services";
import DialogCloseButton from "../../../../components/DialogCloseButton";
import { FormAutocomplete, FormTextField } from "../../../../components/fields";
import useDialog from "../../../../hooks/useDialog";
import useMenu from "../../../../hooks/useMenu";
import {formatCurrency} from "../../../../../finance/utils/FormatUtils";
import { BookingAction, RouteParams } from "../../../../types";
import {usePaymentRequestRepository} from "../../../../../finance/api/repositories/PaymentRequestRepository";
interface IFormValues {
    subaction: string | null,
    note: string
}

const ChangeStatusAction = () => {

    const { t } = useTranslation('booking');

    const { id } = useParams<RouteParams>();

    const { getPaymentsRequestsById } = usePaymentRequestRepository();

    const { dispatchGlobalData } = useContext(GlobalContext);

    const menu = useMenu();
    const dialog = useDialog();

    const formDialog = useForm<IFormValues>({
        defaultValues: {
            subaction: null,
            note: ''
        },
        reValidateMode: 'onSubmit',
        resolver: yupResolver(
            yup
                .object({
                    subaction: yup
                        .string()
                        .nullable()
                        .when({
                            is: undefined,
                            then: schema => schema,
                            otherwise: schema => schema
                                .required('message.validation.required'),
                        })
                })),
        shouldUnregister: true
    });

    const bookingQuery = useBookingByIdWithMetaQueryObserver(id);
    const bookingNotesQuery = useBookingNotesQuery(id);

    const execBookingActionMutation = useMutation(
        execBookingAction,
        {
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onSuccess: () => {
                bookingQuery.refetch();
                bookingNotesQuery.refetch();
            },
            onSettled: () => {
                dialog.handleClose()
            }
        }
    );

    const [action, setAction] = useState<BookingAction | null>(null);
    const [subactions, setSubactions] = useState<string[]>([]);
    const [hasNote, setHasNote] = useState<boolean>(false);
    const [dialogText, setDialogText] = useState<string | null>(null);

    const actions = useMemo<BookingAction[]>(() => (
        Object.keys(bookingQuery.data?.permissions.actions ?? {}) as BookingAction[]
    ), [bookingQuery.data]);
    const filteredActions = useMemo<BookingAction[]>(() => (
        actions
            .filter(a => (
                a !== BookingAction.Edit &&
                a !== BookingAction.Book &&
                a !== BookingAction.Blacklist &&
                a !== BookingAction.AssignVehicle &&
                a !== BookingAction.Clone &&
                a !== BookingAction.CreateRA &&
                a !== BookingAction.resendMail &&
                a !== BookingAction.cancelNew &&
                a !== BookingAction.hardEdit &&
                a !== BookingAction.EditRcePending &&
                a !== BookingAction.CreateSubstitution &&
                a !== BookingAction.CreateSubstitutionRecovery &&
                a !== BookingAction.CreateRARecovery &&
                a !== BookingAction.CreateRaKeyless &&
                a !== BookingAction.CreateRaRce &&
                a !== BookingAction.CloneVoid
            ))
    ), [actions])

    const formatRceConfirmOffSystemPaymentMessage = (
      template: string,
      values: Record<string, string | number>
    ): string => {
      return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key]));
    };

    const onClickAction = useCallback((value: BookingAction) => {
        if (!bookingQuery.data) {
            return;
        }
        menu.handleClose();
        setAction(value);
        const valueSubactions = bookingQuery.data?.permissions.actions[value] ?? [];
        setSubactions(valueSubactions);
        if (valueSubactions.length > 0) {
            formDialog.register('subaction');
        }
        switch (value) {
            case BookingAction.Confirm:
                setHasNote(false);
                const booking = bookingQuery?.data?.booking
                if (booking.rce && booking.status == 'PENDING') {
                  getPaymentsRequestsById(booking.paymentCode ?? "").then((response) => {
                    const data = response.result;
                    const preauthAmount = data.amount;
                    const totalToBePaid = booking.totalToBePaid;

                    if (totalToBePaid > preauthAmount) {
                      setDialogText(
                        formatRceConfirmOffSystemPaymentMessage(
                          t("message.info.confirm_rce_booking_action_offSystem_payment"),
                          {
                            preauth: formatCurrency(preauthAmount, "EUR", 0.01),
                            amountDue: formatCurrency(totalToBePaid, "EUR", 0.01),
                            diff: formatCurrency(totalToBePaid - preauthAmount, "EUR", 0.01),
                          }
                        )
                      );
                    } else {
                      setDialogText(t("message.info.confirm_booking_action"));
                    }
                  });
                } else {
                  setDialogText(t("message.info.confirm_booking_action"));
                }
                break;
            case BookingAction.Reject:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t("message.info.reject_booking_action"));
                break;
            case BookingAction.Cancel:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t("message.info.cancel_booking_action"));
                break;
            case BookingAction.Fail:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t("message.info.fail_booking_action"));
                break;
            case BookingAction.Available:
                setHasNote(false);
                setDialogText(t("message.info.available_booking_action"));
                break;
            case BookingAction.Unavailable:
                setHasNote(true);
                formDialog.register('note');
                setDialogText(t("message.info.unavailable_booking_action"));
                break;
            default:
                return;
        }
        dialog.handleShow();
    }, [bookingQuery.data, t]);
    const onClickConfirm = useCallback((data: IFormValues) => {
        if (
            !bookingQuery.data ||
            !action
        ) {
            return;
        }
        execBookingActionMutation.mutate({
            bookingId: bookingQuery.data.booking.id,
            action: action,
            subaction: data.subaction || undefined,
            note: data.note || undefined,
            lastUpdateTime: bookingQuery.data.booking.requestUpdateDate
        });
    }, [
        bookingQuery.data,
        action,
    ]);

    return (
        <>
            <Button
                onClick={menu.handleShow}
                endIcon={<MoreVertRounded />}
                disabled={filteredActions.length === 0}
            >
                {t('label.changeStatus')}
            </Button>
            <Menu
                open={menu.open}
                anchorEl={menu.anchorEl}
                onClose={menu.handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {filteredActions
                    .map(a => (
                        <MenuItem key={a} onClick={() => onClickAction(a)}>
                            {t(`action.${a}`)}
                        </MenuItem>
                    ))
                }
            </Menu>
            <Dialog
                open={dialog.open}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={dialog.handleClose} />
                <DialogTitle>{t('label.statusChange')}</DialogTitle>
                <DialogContent>
                    <DialogContentText style={{ whiteSpace: "pre-line" }}>
                        {dialogText}
                    </DialogContentText>
                    {subactions.length > 0 &&
                        <FormAutocomplete<IFormValues>
                            name="subaction"
                            control={formDialog.control}
                            label={action === BookingAction.Cancel ? t('label.requestedBy') : t("label.reason")}
                            options={subactions}
                            getOptionLabel={(option) => t(`action.${option}`)}
                        />
                    }
                    {hasNote &&
                        <FormTextField<IFormValues>
                            name="note"
                            control={formDialog.control}
                            label={t("label.note")}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={dialog.handleClose}
                    >
                        {t('label.back')}
                    </Button>
                    <Button onClick={formDialog.handleSubmit(onClickConfirm)}>
                        {t('label.save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomLoader loading={execBookingActionMutation.isLoading} />
        </>
    );
};

export default ChangeStatusAction;
